import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Per-workbench terminal layout state. Mirrors the persistence semantics of
// apps/web/terminal.js so the React app honors the same per-workbench layout
// keys (`agentguard-terminal-<wbId>::<suffix>`) and the same six modes:
//   tabs | grid-auto | grid-1..4 | tiling | floating | canvas

export type LayoutMode =
  | "tabs"
  | "grid-auto"
  | "grid-1"
  | "grid-2"
  | "grid-3"
  | "grid-4"
  | "tiling"
  | "floating"
  | "canvas";

export interface TilingLeaf {
  type: "leaf";
  id: string;
}
export interface TilingSplit {
  type: "split";
  dir: "h" | "v";
  ratio: number;
  left: TilingNode;
  right: TilingNode;
}
export type TilingNode = TilingLeaf | TilingSplit;

export interface FloatingPosition {
  left: number;
  top: number;
  width: number;
  height: number;
  z: number;
}

const MIN_DENSITY = 320;
const MAX_DENSITY = 900;
const DEFAULT_DENSITY = 480;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2.0;

const DEFAULT_CANVAS_TILE_W = 640;
const DEFAULT_CANVAS_TILE_H = 420;
const DEFAULT_CANVAS_COLS = 4;

export function wbKey(wbId: string, suffix: string): string {
  return `agentguard-terminal-${wbId || "wb-default"}::${suffix}`;
}

function read<T>(key: string, fallback: T, parse: (raw: string) => T | null): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = parse(raw);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
  } catch {
    // hardened webviews may block localStorage
  }
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

// ── Tiling tree ops ──────────────────────────────────────────────────────────

function collectLeafIds(node: TilingNode | null): string[] {
  if (!node) return [];
  if (node.type === "leaf") return [node.id];
  return collectLeafIds(node.left).concat(collectLeafIds(node.right));
}

function addLeafToTree(
  tree: TilingNode | null,
  id: string,
  parentDir: "h" | "v" | null
): TilingNode {
  if (!tree) return { type: "leaf", id };
  if (tree.type === "leaf") {
    const dir: "h" | "v" = parentDir === "v" ? "h" : "v";
    return { type: "split", dir, ratio: 0.5, left: tree, right: { type: "leaf", id } };
  }
  return { ...tree, right: addLeafToTree(tree.right, id, tree.dir) };
}

function removeLeafFromTree(node: TilingNode | null, id: string): TilingNode | null {
  if (!node) return null;
  if (node.type === "leaf") return node.id === id ? null : node;
  const left = removeLeafFromTree(node.left, id);
  const right = removeLeafFromTree(node.right, id);
  if (!left) return right;
  if (!right) return left;
  return { ...node, left, right };
}

function reconcileTilingTree(tree: TilingNode | null, currentIds: Set<string>): TilingNode | null {
  let next = tree;
  for (const id of collectLeafIds(next)) {
    if (!currentIds.has(id)) next = removeLeafFromTree(next, id);
  }
  const present = new Set(collectLeafIds(next));
  for (const id of currentIds) {
    if (!present.has(id)) next = addLeafToTree(next, id, null);
  }
  return next;
}

export interface PositionedTile {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
  z?: number;
}

export interface TilingSplitter {
  path: number[];
  dir: "h" | "v";
  ratio: number;
  left: number;
  top: number;
  width: number;
  height: number;
  containerSize: number;
}

export function layoutTilingTree(
  node: TilingNode | null,
  rect: { left: number; top: number; width: number; height: number },
  out: { tiles: PositionedTile[]; splits: TilingSplitter[] },
  path: number[] = []
): void {
  if (!node) return;
  if (node.type === "leaf") {
    out.tiles.push({ id: node.id, ...rect });
    return;
  }
  const g = 6;
  if (node.dir === "v") {
    const lw = Math.max(40, rect.width * node.ratio - g / 2);
    const rw = Math.max(40, rect.width * (1 - node.ratio) - g / 2);
    layoutTilingTree(
      node.left,
      { left: rect.left, top: rect.top, width: lw, height: rect.height },
      out,
      [...path, 0]
    );
    layoutTilingTree(
      node.right,
      { left: rect.left + lw + g, top: rect.top, width: rw, height: rect.height },
      out,
      [...path, 1]
    );
    out.splits.push({
      path,
      dir: "v",
      ratio: node.ratio,
      left: rect.left + lw,
      top: rect.top,
      width: g,
      height: rect.height,
      containerSize: rect.width,
    });
  } else {
    const th = Math.max(40, rect.height * node.ratio - g / 2);
    const bh = Math.max(40, rect.height * (1 - node.ratio) - g / 2);
    layoutTilingTree(
      node.left,
      { left: rect.left, top: rect.top, width: rect.width, height: th },
      out,
      [...path, 0]
    );
    layoutTilingTree(
      node.right,
      { left: rect.left, top: rect.top + th + g, width: rect.width, height: bh },
      out,
      [...path, 1]
    );
    out.splits.push({
      path,
      dir: "h",
      ratio: node.ratio,
      left: rect.left,
      top: rect.top + th,
      width: rect.width,
      height: g,
      containerSize: rect.height,
    });
  }
}

export interface UseTerminalLayoutResult {
  layout: LayoutMode;
  setLayout: (next: LayoutMode) => void;
  density: number;
  setDensity: (value: number) => void;
  canvasZoom: number;
  setCanvasZoom: (value: number) => void;
  resetCanvasZoom: () => void;
  fitCanvasZoom: (containerW: number, containerH: number, tileCount: number) => void;
  canvasCols: number;
  canvasTileW: number;
  canvasTileH: number;
  tilingTree: TilingNode | null;
  setTilingRatio: (path: number[], ratio: number) => void;
  rebuildTilingTree: (currentIds: Set<string>) => void;
  floatingPositions: Record<string, FloatingPosition>;
  setFloatingPosition: (id: string, position: FloatingPosition) => void;
  raiseFloating: (id: string) => void;
  pruneFloatingPositions: (currentIds: Set<string>) => void;
  columnFractions: number[];
  setColumnFractions: (n: number, fractions: number[]) => void;
  rowFractions: number[];
  setRowFractions: (m: number, fractions: number[]) => void;
}

export function useTerminalLayout(wbId: string): UseTerminalLayoutResult {
  const [layout, setLayoutState] = useState<LayoutMode>(() =>
    read(wbKey(wbId, "layout"), "grid-auto" as LayoutMode, (raw) => raw as LayoutMode)
  );
  const [density, setDensityState] = useState<number>(() =>
    read(wbKey(wbId, "mintile"), DEFAULT_DENSITY, (raw) =>
      clamp(parseInt(raw, 10), MIN_DENSITY, MAX_DENSITY)
    )
  );
  const [canvasZoom, setCanvasZoomState] = useState<number>(() =>
    read(wbKey(wbId, "canvaszoom"), 1.0, (raw) => clamp(parseFloat(raw), MIN_ZOOM, MAX_ZOOM))
  );
  const [tilingTree, setTilingTreeState] = useState<TilingNode | null>(() =>
    read(
      wbKey(wbId, "tilingtree"),
      null as TilingNode | null,
      (raw) => JSON.parse(raw) as TilingNode
    )
  );
  const [floatingPositions, setFloatingPositionsState] = useState<Record<string, FloatingPosition>>(
    () =>
      read(
        wbKey(wbId, "floatingpos"),
        {} as Record<string, FloatingPosition>,
        (raw) => JSON.parse(raw) as Record<string, FloatingPosition>
      )
  );
  const nextZRef = useRef<number>(
    Math.max(10, ...Object.values(floatingPositions).map((p) => p.z || 0)) + 1
  );
  const [columnFractions, setColumnFractionsState] = useState<number[]>([]);
  const [rowFractions, setRowFractionsState] = useState<number[]>([]);

  // Re-load persisted values when the active workbench changes. The user
  // expects each workbench to keep its own layout, density, tiling tree, etc.
  useEffect(() => {
    setLayoutState(
      read(wbKey(wbId, "layout"), "grid-auto" as LayoutMode, (raw) => raw as LayoutMode)
    );
    setDensityState(
      read(wbKey(wbId, "mintile"), DEFAULT_DENSITY, (raw) =>
        clamp(parseInt(raw, 10), MIN_DENSITY, MAX_DENSITY)
      )
    );
    setCanvasZoomState(
      read(wbKey(wbId, "canvaszoom"), 1.0, (raw) => clamp(parseFloat(raw), MIN_ZOOM, MAX_ZOOM))
    );
    setTilingTreeState(
      read(
        wbKey(wbId, "tilingtree"),
        null as TilingNode | null,
        (raw) => JSON.parse(raw) as TilingNode
      )
    );
    setFloatingPositionsState(
      read(
        wbKey(wbId, "floatingpos"),
        {} as Record<string, FloatingPosition>,
        (raw) => JSON.parse(raw) as Record<string, FloatingPosition>
      )
    );
    setColumnFractionsState([]);
    setRowFractionsState([]);
  }, [wbId]);

  const setLayout = useCallback(
    (next: LayoutMode) => {
      setLayoutState(next);
      write(wbKey(wbId, "layout"), next);
    },
    [wbId]
  );

  const setDensity = useCallback(
    (value: number) => {
      const clamped = clamp(value, MIN_DENSITY, MAX_DENSITY);
      setDensityState(clamped);
      write(wbKey(wbId, "mintile"), String(clamped));
    },
    [wbId]
  );

  const setCanvasZoom = useCallback(
    (value: number) => {
      const clamped = clamp(value, MIN_ZOOM, MAX_ZOOM);
      setCanvasZoomState(clamped);
      write(wbKey(wbId, "canvaszoom"), String(clamped));
    },
    [wbId]
  );

  const resetCanvasZoom = useCallback(() => setCanvasZoom(1.0), [setCanvasZoom]);

  const fitCanvasZoom = useCallback(
    (containerW: number, containerH: number, tileCount: number) => {
      const gap = 14,
        pad = 18;
      const cols = Math.max(1, DEFAULT_CANVAS_COLS);
      const rows = Math.max(1, Math.ceil(tileCount / cols));
      const tw = pad * 2 + cols * DEFAULT_CANVAS_TILE_W + (cols - 1) * gap;
      const th = pad * 2 + rows * DEFAULT_CANVAS_TILE_H + (rows - 1) * gap;
      if (tw <= 0 || th <= 0) return;
      setCanvasZoom(Math.min((containerW - 8) / tw, (containerH - 8) / th));
    },
    [setCanvasZoom]
  );

  const setTilingRatio = useCallback(
    (path: number[], ratio: number) => {
      setTilingTreeState((current) => {
        if (!current) return current;
        const cloned = cloneTree(current);
        let node: TilingNode = cloned;
        for (const step of path) {
          if (node.type !== "split") return current;
          node = step === 0 ? node.left : node.right;
        }
        // path leads to the target split parent; ratio is on the parent itself
        // (we walked TO the split, not past it)
        if ((node as TilingNode).type !== "split") return current;
        (node as TilingSplit).ratio = Math.max(0.1, Math.min(0.9, ratio));
        write(wbKey(wbId, "tilingtree"), cloned);
        return cloned;
      });
    },
    [wbId]
  );

  const rebuildTilingTree = useCallback(
    (currentIds: Set<string>) => {
      setTilingTreeState((current) => {
        const next = reconcileTilingTree(current, currentIds);
        if (JSON.stringify(next) === JSON.stringify(current)) return current;
        write(wbKey(wbId, "tilingtree"), next);
        return next;
      });
    },
    [wbId]
  );

  const setFloatingPosition = useCallback(
    (id: string, position: FloatingPosition) => {
      setFloatingPositionsState((current) => {
        const next = { ...current, [id]: position };
        write(wbKey(wbId, "floatingpos"), next);
        return next;
      });
    },
    [wbId]
  );

  const raiseFloating = useCallback(
    (id: string) => {
      setFloatingPositionsState((current) => {
        const existing = current[id];
        if (!existing) return current;
        const next = { ...current, [id]: { ...existing, z: nextZRef.current++ } };
        write(wbKey(wbId, "floatingpos"), next);
        return next;
      });
    },
    [wbId]
  );

  const pruneFloatingPositions = useCallback(
    (currentIds: Set<string>) => {
      setFloatingPositionsState((current) => {
        let dirty = false;
        const next: Record<string, FloatingPosition> = {};
        for (const [id, pos] of Object.entries(current)) {
          if (currentIds.has(id)) next[id] = pos;
          else dirty = true;
        }
        if (!dirty) return current;
        write(wbKey(wbId, "floatingpos"), next);
        return next;
      });
    },
    [wbId]
  );

  const setColumnFractions = useCallback(
    (n: number, fractions: number[]) => {
      if (fractions.length !== n) return;
      setColumnFractionsState(fractions);
      write(wbKey(wbId, `colfracs-${n}`), fractions);
    },
    [wbId]
  );

  const setRowFractions = useCallback(
    (m: number, fractions: number[]) => {
      if (fractions.length !== m) return;
      setRowFractionsState(fractions);
      write(wbKey(wbId, `rowfracs-${m}`), fractions);
    },
    [wbId]
  );

  return useMemo(
    () => ({
      layout,
      setLayout,
      density,
      setDensity,
      canvasZoom,
      setCanvasZoom,
      resetCanvasZoom,
      fitCanvasZoom,
      canvasCols: DEFAULT_CANVAS_COLS,
      canvasTileW: DEFAULT_CANVAS_TILE_W,
      canvasTileH: DEFAULT_CANVAS_TILE_H,
      tilingTree,
      setTilingRatio,
      rebuildTilingTree,
      floatingPositions,
      setFloatingPosition,
      raiseFloating,
      pruneFloatingPositions,
      columnFractions,
      setColumnFractions,
      rowFractions,
      setRowFractions,
    }),
    [
      layout,
      setLayout,
      density,
      setDensity,
      canvasZoom,
      setCanvasZoom,
      resetCanvasZoom,
      fitCanvasZoom,
      tilingTree,
      setTilingRatio,
      rebuildTilingTree,
      floatingPositions,
      setFloatingPosition,
      raiseFloating,
      pruneFloatingPositions,
      columnFractions,
      setColumnFractions,
      rowFractions,
      setRowFractions,
    ]
  );
}

function cloneTree(node: TilingNode): TilingNode {
  if (node.type === "leaf") return { type: "leaf", id: node.id };
  return {
    type: "split",
    dir: node.dir,
    ratio: node.ratio,
    left: cloneTree(node.left),
    right: cloneTree(node.right),
  };
}
